import React, { useEffect, useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { downloadCSV, downloadTablePDF, downloadXLSX } from "@/lib/tableUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function districtMemberRows(members, includeDistrict = true) {
  const header = [
    "KELTA ID", "Name", "Email ID", "Phone Number",
    ...(includeDistrict ? ["District"] : []),
    "Local Body", "Local Body Type", "Paid", "Active",
  ];
  const body = members.map(m => [
    m.membership_id || "",
    m.full_name || "",
    m.email || "",
    m.mobile || "",
    ...(includeDistrict ? [m.district || ""] : []),
    m.local_body_name || "",
    m.local_body_type || "",
    m.payment_status === "paid" ? "Paid" : (m.payment_status || ""),
    m.membership_status === "active" ? "Active" : (m.membership_status || ""),
  ]);
  return [header, ...body];
}

export default function DistrictMembersDialog({ district, open, onOpenChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    if (!open || !district) return;
    setSearch("");
    setLoading(true);
    api.get("/admin/members/paid-active", { params: { district } })
      .then(r => setMembers(r.data.rows || []))
      .catch(e => {
        setMembers([]);
        toast.error(e?.response?.data?.detail || "Could not load district members");
      })
      .finally(() => setLoading(false));
  }, [open, district]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return members;
    return members.filter(m => [
      m.membership_id, m.full_name, m.email, m.mobile,
      m.local_body_name, m.local_body_type,
    ].some(v => (v || "").toLowerCase().includes(s)));
  }, [members, search]);

  const slug = (district || "district").toLowerCase().replace(/\s+/g, "-");
  const filenameBase = `kelta_${slug}_paid_active_members`;
  const exportRows = () => districtMemberRows(filtered, false);
  const exportDistrict = (kind) => {
    if (!filtered.length) return;
    setExporting(kind);
    const title = `KELTA — ${district} Paid & Active Members`;
    if (kind === "csv") downloadCSV(exportRows(), `${filenameBase}.csv`);
    if (kind === "xlsx") downloadXLSX(exportRows(), `${filenameBase}.xlsx`, district);
    if (kind === "pdf") downloadTablePDF(exportRows(), `${filenameBase}.pdf`, {
      title,
      subtitle: `${filtered.length} member${filtered.length === 1 ? "" : "s"}${search ? ` · Filter: ${search}` : ""}`,
      brandColor: [0, 94, 184],
      accentColor: [255, 152, 0],
    });
    setExporting("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] flex flex-col" data-testid="district-members-dialog">
        <DialogHeader>
          <DialogTitle>{district} — Paid &amp; Active Members</DialogTitle>
          <DialogDescription>
            Detailed member list for {district}. Search filters the table and the district export.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between border-b border-border pb-3">
          <div className="relative w-full lg:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search KELTA ID, name, email, phone or local body…"
              className="pl-9"
              data-testid="district-members-search"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" data-testid="district-members-count">
              {filtered.length} of {members.length} members
            </Badge>
            <Button variant="outline" size="sm" onClick={() => exportDistrict("csv")} disabled={loading || !filtered.length || !!exporting} data-testid="district-members-export-csv">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportDistrict("xlsx")} disabled={loading || !filtered.length || !!exporting} data-testid="district-members-export-xlsx">
              <Download className="w-4 h-4 mr-1.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportDistrict("pdf")} disabled={loading || !filtered.length || !!exporting} data-testid="district-members-export-pdf">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto rounded-md border border-border" data-testid="district-members-table-wrap">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>KELTA ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email ID</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Local Body</TableHead>
                <TableHead>Local Body Type</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Loading members…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground" data-testid="district-members-empty">No paid and active members found.</TableCell></TableRow>
              ) : filtered.map(m => (
                <TableRow key={m.id} data-testid={`district-member-row-${m.id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{m.membership_id || "—"}</TableCell>
                  <TableCell className="font-medium whitespace-nowrap">{m.full_name || "—"}</TableCell>
                  <TableCell className="text-xs">{m.email || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{m.mobile || "—"}</TableCell>
                  <TableCell>{m.local_body_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{m.local_body_type || "—"}</TableCell>
                  <TableCell><Badge className="bg-brand-green text-white">Paid</Badge></TableCell>
                  <TableCell><Badge className="bg-brand-blue text-white">Active</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
